
/**
 * Returns an action that resets the store's value any time the targeted element's `class` attribute changes.
 * @param {import("../stores/svelte/public").Writable<string[]>} store
 * @returns {import("../public").ActionCallback}
 */
export function observeClassChanges(store) {
	return (element, state) => {
		const observer = state.observer ?? new MutationObserver(entries => {
			for (let entry of entries) {
				if (element === entry.target && entry.attributeName === "class") {
					store.set(Array.from(element.classList));
				}
			}
		});
		state.observer = observer;
		observer.observe(element, { attributeFilter: ["class"] });

		return () => {
			observer.disconnect();
		}
	}
}

/**
 * Returns an action that resets the store's value any time the targeted element's `class` attribute changes.
 * @param {import("../stores/svelte/public").Writable<HTMLElement[]>} store
 * @param {string} selector The selector used to filter affected nodes.
 * @param {((record: import("../public").NodeTreeMutationRecord) => void)?} callback An optional callback to be called for every mutation record. Includes only the `added` and `removed` HTMLElements matching the `selector`.
 * @returns {import("../public").ActionCallback}
 */
export function observeMatchingSubtree(store, selector, callback = null) {
	return (element, state) => {
		/** @type {MutationObserver} */
		const observer = state.observer ?? new MutationObserver(entries => {
			/** @type {import("../public").NodeTreeMutationRecord} */
			const record = {
				added: [],
				removed: [],
			};
			/**
			 * @template {Node} T
			 * @param {T} x
			 * @returns {x is HTMLElement}
			 */
			function predicate(x) {
				return x instanceof HTMLElement && x.matches(selector);
			};
			for (let entry of entries) {
				[...entry.addedNodes].filter(predicate).forEach(n => record.added.push(n));
				[...entry.removedNodes].filter(predicate).forEach(n => record.removed.push(n));
				if (callback && (record.added.length || record.removed.length)) {
					callback(record);
				}
			}
			store.update(value => {
				for (let item of record.added) {
					value.push(/** @type {HTMLElement} */ (item));
				}
				for (let item of record.removed) {
					const index = value.findIndex(x => x === item);
					value.splice(index, 1);
				}
				return value;
			});
		});
		state.observer = observer;
		if (element) {
			observer.observe(element, { childList: true, subtree: true });
		}

		return () => {
			observer.disconnect();
		}
	}
}
